import React from "react";
import { T } from "../../app/tokens.js";
import { InternalLink } from "../../components/InternalLink.jsx";
import { statusPillStyle } from "../capacity/capacityStyles.js";
import { marketCountryIdentity, marketEventLabel, marketDateDisclosure } from "./marketsListModel.js";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";
import { serializeCapacityState, parseCapacityState } from "../capacity/capacityUrl.js";
import { LIST_PRESENTATION } from "../../v2/components/listPresentation.js";
import { STATUS_LABEL } from "../capacity/capacityModel.js";
import { CAPACITY_STATUS_DEFINITION } from "../../services/seatAccounting.js";

// Initial fallback only; native collection measurements own the actual minima.
// Market · Hub · Region · Strategy · Plan · Capacity · Offices · People ·
// Expansion · Annual cost · Next date. No empty surplus track.
const DATA_WIDTHS = [190, 34, 50, 94, 80, 100, 54, 115, 88, 104, 150];
const widthsFor = cost => DATA_WIDTHS.filter((_, i) => cost || i !== 9);
const templateFor = cost => widthsFor(cost).map(width => `minmax(${width}px, 1fr)`).join(" ");
export const MARKETS_COLLECTION_COL = templateFor(true);
export const MARKETS_COLLECTION_COL_NO_COST = templateFor(false);
export const marketsCollectionWidth = cost => widthsFor(cost).reduce((sum, width) => sum + width, 0);
export const CAPACITY_PILL_LABELS = [...Object.values(STATUS_LABEL), "Not in Capacity", "Read failed", "Loading"];
// Collection-only wording approved for the narrow list. Source/model labels
// remain unchanged and stay available in both the tooltip and accessible name.
const SHORT_CAPACITY_LABEL = {
  "Under 3 months history": "New · <3 mo",
  "Not in Capacity": "Excluded",
  "Read failed": "Error",
  Loading: "Loading…",
};

export function MarketCountry({ market, collection = false, presenceTitle = "" }) {
  const identity = marketCountryIdentity(market.locations);
  if (collection) return <span data-collection-name="" style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
    <span data-testid={`market-name-line-${market.id}`} style={LIST_PRESENTATION.name}>{market.name}</span>
    {identity.country && <span data-testid={`market-country-${market.id}`}
      title={`${presenceTitle}\nPrimary office: ${identity.primaryOfficeId ?? "None"}${identity.countries.length > 1 ? `\nCountries: ${identity.countries.join(", ")}` : ""}`}
      style={{ color: LIST_PRESENTATION.secondary.color }}> · {identity.country}</span>}
  </span>;
  return <span title={`${market.name}${identity.country ? ` · ${identity.country}` : ""}\nPrimary office: ${identity.primaryOfficeId ?? "None"}${identity.countries.length > 1 ? `\nCountries: ${identity.countries.join(", ")}` : ""}`}>
    <span data-testid={`market-name-line-${market.id}`} style={LIST_PRESENTATION.name}>{market.name}</span>
    {identity.country && <span data-testid={`market-country-${market.id}`} style={{ color: LIST_PRESENTATION.secondary.color }}> · {identity.country}</span>}
  </span>;
}

export function MarketsCapacityPill({ market, compact }) {
  const ladder = market.capacityLadder;
  if (["excluded", "no_seats"].includes(ladder?.cell?.status)) {
    return <span data-testid={`market-ladder-${market.id}`} style={{ color: T.mid }}>{ladder.label}</span>;
  }
  const sourceLabel = ladder?.status === "ok" ? ladder.label
    : ladder?.status === "excluded" ? "Not in Capacity" : ladder?.status === "error" ? "Read failed" : "Loading";
  const label = SHORT_CAPACITY_LABEL[sourceLabel] ?? sourceLabel;
  const title = `${CAPACITY_STATUS_DEFINITION}. ${ladder?.month ?? "Current month"} baseline: ${sourceLabel}. ${ladder?.error || ladder?.disclosure || ""}`;
  const state = { ...parseCapacityState(""), open: [market.id], q: market.name, focus: ladder?.month ?? null };
  return <InternalLink href={`/places/capacity${serializeCapacityState(state)}`} data-testid={`market-ladder-${market.id}`}
    className="markets-capacity-pill"
    title={title} aria-label={`${sourceLabel}: open Capacity for ${market.name}. ${title}`} onClick={e => e.stopPropagation()}
    style={{ ...statusPillStyle(ladder?.cell?.status), fontSize: compact ? T.fsXxs : T.fsXs,
      padding: compact ? "0 8px" : "1px 8px", height: compact ? 18 : 22,
      width: "var(--markets-pill-width, max-content)",
      boxSizing: "border-box", display: "inline-grid", gridTemplateAreas: '"label"', justifyItems: "center", textDecoration: "none" }}>
    <span style={{ gridArea: "label" }}>{label}{ladder?.cell?.demandCoverage?.missing?.length ? " · partial" : ""}</span>
  </InternalLink>;
}

export function MarketsAnnualCost({ market }) {
  const missing = market.rentIncompleteOffices ?? [];
  const known = typeof market.totalAnnualCost === "number" && Number.isFinite(market.totalAnnualCost);
  const title = `${known ? `$${market.totalAnnualCost.toLocaleString("en-US")} USD` : "Annual cost unknown"}; all-in, USD-normalized.${missing.length ? ` Partial: no rent on file for ${missing.map(o => o.locationAlias || o.locationId).join(", ")}.` : ""}`;
  return <span data-testid={`market-annual-cost-${market.id}`} title={title} style={LIST_PRESENTATION.numeric}>
    {known ? fmtKpiUsd(market.totalAnnualCost) : "–"}{missing.length > 0 && <span style={{ color: T.ragAmber }}> †</span>}
  </span>;
}

export function MarketsNextDate({ selection, marketId }) {
  const event = selection?.next;
  const marker = event?.provenance === "calculated" ? "calc" : event?.provenance === "ambiguous" ? "unverified" : null;
  const date = event ? new Date(`${event.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null;
  const empty = selection?.status !== "ok" ? "Unavailable" : selection.ambiguities.length ? "Needs review" : "None";
  return <span data-testid={`market-next-date-${marketId}`} title={marketDateDisclosure(selection)}
    style={{ ...LIST_PRESENTATION.numeric, display: "flex", alignItems: "center", gap: 4, width: "max-content", whiteSpace: "nowrap", flexShrink: 0 }}>
    {event ? <>
      <span style={{ flexShrink: 0 }}>{marketEventLabel(event)}</span>
      <span style={{ flexShrink: 0 }}>{date}</span>
      {marker && <small aria-label={marker === "unverified" ? "Source unverified" : "Calculated date"}
        title={`${marker === "unverified" ? "Source unverified" : "Calculated date"} · ${marketDateDisclosure(selection)}`}
        style={{ flexShrink: 0, color: T.ragAmber }}>{marker === "unverified" ? "?" : marker}</small>}
      {selection.status !== "ok" && <small title="Incomplete lease evidence" style={{ flexShrink: 0 }}>†</small>}
    </> : empty}
  </span>;
}
